/**
 * Custom business-event tag taxonomy for Spheeres.UI (legacy js/ app).
 *
 * Single source of truth for the `Subject_Verb` tags passed to
 * `tracker.event(tag, payload?)`. The replay viewer colors each marker by
 * the tag's VERB token via the verb tokenizer in the session-replay
 * replay-ui (`useCustomEventColor.ts`) — so the verb here decides the color,
 * not the subject. Each constant's trailing comment names the color it
 * resolves to; do not rename a tag without checking the verb still lands in
 * the intended category (whole-token match, camelCase-aware, first category
 * in the list below that matches any token wins).
 *
 * NOTE on auth: the recorder in the legacy app is started post-login
 * (UserManager initSessionRecorder), so login-page events would be dropped
 * (no recorder yet). Only logout is emittable; login success/failure are
 * intentionally absent.
 *
 * Color reference (category order = match priority; verb token -> color):
 *   emerald success/complete(d)/finished/done/paid/confirmed/approved/ok
 *   red     error/fail(ed)/failure/exception/reject(ed)/denied/crash/invalid/timeout
 *   blue    start(ed)/begin/began/init/launch(ed)/open(ed)/new/add(ed)/create(d)
 *   orange  update(d)/save(d)/edit(ed)/change(d)/modify/modified/submit(ted)/send/sent/post(ed)
 *   pink    delete(d)/remove(d)/cancel(led)/abandon(ed)/drop(ped)/close(d)
 *   purple  click(ed)/select(ed)/chosen/interact(ed)/tap(ped)/hover(ed)
 *   teal    view(ed)/seen/show(n)/impression/display(ed)/render(ed)/use(d)
 *   amber   search(ed)/query/queried/filter(ed)/sort(ed)/find/found
 */
export const SessionEvents = {
	// ── Auth ──────────────────────────────────────────────────────────────
	LoggedOut: 'session_closed',         // closed    -> 🌸 pink

	// ── Navigation ────────────────────────────────────────────────────────
	ScreenOpened: 'screen_opened',       // opened    -> 🔵 blue
	RecordViewed: 'record_viewed',       // viewed    -> 🟦 teal

	// ── Record lifecycle ──────────────────────────────────────────────────
	RecordCreated: 'record_created',     // created   -> 🔵 blue
	RecordSaved: 'record_saved',         // saved     -> 🟠 orange
	RecordDeleted: 'record_deleted',     // deleted   -> 🌸 pink
	RecordDeleteFailed: 'record_delete_failed', // failed (beats delete) -> 🔴 red

	// ── Grid search / filter / sort ───────────────────────────────────────
	GridSearched: 'grid_searched',       // searched  -> 🟡 amber
	GridFiltered: 'grid_filtered',       // filtered  -> 🟡 amber
	GridSorted: 'grid_sorted',           // sorted    -> 🟡 amber

	// ── Interaction ───────────────────────────────────────────────────────
	RowSelected: 'row_selected',         // selected  -> 🟣 purple

	// ── Execution ─────────────────────────────────────────────────────────
	ExportCompleted: 'export_completed', // completed -> 🟢 emerald

	// ══ Controls (Tier 1) ════════════════════════════════════════════════
	// Buttons — one emit at GenericButton.Click() covers every button control.
	ButtonClicked: 'button_clicked',     // clicked   -> 🟣 purple

	// Security / audit (grant-revoke across the security controls)
	PermissionGranted: 'permission_granted', // granted -> 🔵 blue
	PermissionRevoked: 'permission_revoked', // revoked -> 🌸 pink
	SecurityResetConfirmed: 'security_reset_confirmed', // confirmed -> 🟢 emerald

	// Linking
	RecordLinked: 'record_linked',       // linked    -> 🔵 blue
	RecordUnlinked: 'record_unlinked',   // unlinked  -> 🌸 pink

	// Mail
	EmailSent: 'email_sent',             // sent      -> 🟠 orange
	EmailDeleted: 'email_deleted',       // deleted   -> 🌸 pink

	// Step / process
	StepFinished: 'step_finished',       // finished  -> 🟢 emerald

	// Designers
	CanvasSaved: 'canvas_saved',         // saved     -> 🟠 orange
	CanvasDeleted: 'canvas_deleted',     // deleted   -> 🌸 pink
	CanvasExecuted: 'canvas_executed',   // executed  -> 🟦 teal

	// Basket
	BasketCreated: 'basket_created',     // created   -> 🔵 blue
	BasketDeleted: 'basket_deleted',     // deleted   -> 🌸 pink

	// Kanban
	KanbanSearched: 'kanban_searched',   // searched  -> 🟡 amber
	CardMoved: 'card_moved',             // moved     -> 🟠 orange

	// Timer
	TimerStarted: 'timer_started',       // started   -> 🔵 blue
	TimerStopped: 'timer_stopped',       // stopped   -> 🌸 pink
} as const;

export type SessionEventTag = (typeof SessionEvents)[keyof typeof SessionEvents];
